import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';

const TIERS = [
  {
    name: 'Corridor Pilot',
    price: '$24,500',
    cadence: 'fixed fee · 6 weeks',
    pitch: 'Bounded pilot for a single corridor (500–800 ramps).',
    features: [
      'Calibration validation (Week 1)',
      'Field capture, 2 surveyors (Weeks 2–4)',
      'Analysis + APCI scoring (Week 5)',
      'Six-artifact deliverable bundle (Week 6)',
      '50% refund if <100 defensible findings',
    ],
    cta: 'Recommended pilot',
    accent: 'gold',
  },
  {
    name: 'Jurisdiction-Wide',
    price: '$95,000+',
    cadence: 'scoped per county',
    pitch: 'Full county-wide assessment using VDOT inventory + targeted field capture.',
    features: [
      'Full VDOT inventory analysis',
      'Targeted field capture of priority corridors',
      'County-wide APCI scoring',
      'Full ADA Transition Plan refresh',
      'AGOL feature layer publishing',
      'Quarterly data refresh year 1',
    ],
    cta: 'Most popular',
    accent: 'navy',
  },
  {
    name: 'Statewide / Multi-County',
    price: 'Contact',
    cadence: 'enterprise',
    pitch: 'For state DOTs, regional planning commissions, or multi-jurisdiction MOUs.',
    features: [
      'Multi-county capture orchestration',
      'Statewide rollup APCI scoring',
      'Custom dashboards + portal',
      'Annual third-party methodology audit',
      'Designated AtlasGeo program manager',
    ],
    cta: 'Talk to us',
    accent: 'teal',
  },
];

const accents = {
  gold: { border: 'border-[#DBC078]', tag: 'bg-[#DBC078] text-[#0a0a0a]', btn: 'bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a]' },
  navy: { border: 'border-[#0a0a0a]', tag: 'bg-[#0a0a0a] text-white',     btn: 'bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white' },
  teal: { border: 'border-[#1F7A8C]', tag: 'bg-[#1F7A8C] text-white',     btn: 'bg-[#1F7A8C] hover:bg-[#176278] text-white' },
};

export default function Pricing() {
  return (
    <div className="space-y-8">
      <div className="text-center max-w-2xl mx-auto py-6">
        <div className="text-xs uppercase tracking-widest text-[#1F7A8C] mb-2">Pricing</div>
        <h1 className="text-4xl text-[#0a0a0a] font-serif-display mb-3">Pilot first. Scale on results.</h1>
        <p className="text-stone-600 leading-relaxed">
          Every engagement starts with a bounded pilot. No SaaS subscriptions, no per-seat fees, no vendor
          lock-in. SDVOSB / MBE, eligible for federal pass-through and Virginia small-business set-aside
          procurement.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((t) => {
          const a = accents[t.accent];
          return (
            <div key={t.name} className={`bg-white border-t-4 ${a.border} border border-stone-200 p-6 flex flex-col`}>
              <div className={`inline-block self-start text-[10px] uppercase tracking-widest px-2 py-1 ${a.tag} mb-3`}>{t.cta}</div>
              <h2 className="text-2xl text-[#0a0a0a] font-serif-display">{t.name}</h2>
              <div className="text-3xl text-[#0a0a0a] font-serif-display mt-3">{t.price}</div>
              <div className="text-xs text-stone-500 uppercase tracking-wider">{t.cadence}</div>
              <p className="text-sm text-stone-700 mt-3 mb-4 leading-relaxed">{t.pitch}</p>
              <ul className="space-y-2 text-sm text-stone-700 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2"><CheckCircle2 size={14} className="text-[#1F7A8C] mt-0.5 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
              <a href="mailto:info@atlasgeo.ai?subject=AtlasPath%20pilot%20inquiry" className={`mt-6 ${a.btn} font-medium px-4 py-2 text-sm uppercase tracking-wider text-center`}>
                Request scope
              </a>
            </div>
          );
        })}
      </div>

      <div className="bg-stone-50 border border-stone-200 p-6 max-w-3xl mx-auto text-sm text-stone-700 leading-relaxed">
        <div className="font-medium text-[#0a0a0a] mb-2">Federal funding pathways</div>
        <p>AtlasPath pilots are typically funded through SS4A, HSIP, CDBG, or RAISE programs, with VDOT match
        available under IIM-LD-55. AtlasGeo provides funding-application support letters on request.</p>
        <div className="mt-4">
          <Link to="/dashboard" className="text-[#1F7A8C] hover:underline">← Back to console</Link>
        </div>
      </div>
    </div>
  );
}
